import { Injectable, OnDestroy, inject } from "@angular/core";
import {firstValueFrom, from, Observable, of, Subscription, timeout} from "rxjs";
import {ModalController} from "@ionic/angular";
import {NavigationEnd, NavigationStart, Router} from "@angular/router";

@Injectable({
  providedIn: 'root'
})
export class SharedModalControllerService implements OnDestroy {
  private _modalCtrl = inject(ModalController);
  private _router = inject(Router);


  currentCloseData: any;
  routerEventSubscription?: Subscription;

  constructor() {
    // Subscribe to router event to close modals in case of back navigation
    this.routerEventSubscription = this._router.events.subscribe(event => {
      if (event instanceof NavigationStart) {
        void this._closeAllOpenedModals();
      }
    });
  }

  ngOnDestroy(): void {
    this.routerEventSubscription?.unsubscribe();
  }

  showModal<T = void>(
    component: any,
    cssClass: string,
    componentProps: Object,
    isWaitForDismiss: boolean = false
  ): Observable<T | void> {
    return from(this._presentModal<T>(component, cssClass, componentProps, isWaitForDismiss));
  }

  /**
   * @name closeModal
   * @description
   * close the modal promise as observable
   * @memberof SharedModalControllerService
   * @returns {Observable<boolean>}
   */
  closeModal(data?: any): Observable<boolean> {
    this.currentCloseData = data;
    return from(this._checkExistingAndDismiss(data));
  }

  private async _closeAllOpenedModals(): Promise<void> {
    const openModals = document.querySelectorAll('ion-modal');
    for (const modal of Array.from(openModals)) {
      if (modal) {
        try {
          void modal.dismiss();
        } catch (error) {
          console.error('Error closing modal:', error);
        }
      }
    }
  }

  /**
   * @name _presentModal
   * @description
   * create the modal
   * @memberof SharedModalControllerService
   * @param component
   * @param cssClass
   * @param componentProps
   * @private
   * @returns {Promise<T | void>}
   */
  private async _presentModal<T = void>(component: any, cssClass: string, componentProps: Object,
                                        isWaitForDismiss: boolean = false): Promise<T | void> {
    const modal = await this._modalCtrl.create({
      component: component,
      cssClass: cssClass,
      componentProps: componentProps,
      backdropDismiss: false
    });

    await modal.present();

    if (isWaitForDismiss) {
      const result = await modal.onDidDismiss<T>();
      return result.data ?? this.currentCloseData;
    }
  }

  /**
   * @name _checkExistingAndDismiss
   * @description
   * check if there is an existing modal and close it or dont launch the close if there is not modal
   * @memberof SharedModalControllerService
   * @private
   * @returns {Promise<boolean>}
   */
  private async _checkExistingAndDismiss(data?: any): Promise<boolean> {
    const existingModal = await this._checkExisting();
    if (existingModal) {
      await this._modalCtrl.dismiss(data);
      return true;
    }
    return false;
  }

  /**
   * @name _checkExisting
   * @description
   * check if there is existing modal
   * @memberof SharedModalControllerService
   * @private
   * @returns {Promise<HTMLIonModalElement | undefined>}
   */
  private async _checkExisting(): Promise<HTMLIonModalElement | undefined> {
    return await this._modalCtrl.getTop();
  }
}
